import { Timestamp } from "firebase/firestore";
import { toISODate } from "../../utils/date";

/**
 * financialRecordToFirestore
 * 
 * Converte dados do formulário React para o formato do Firestore.
 * 
 * Contrato de datas:
 * - Datas de calendário (issueDate, dueDate, competenceDate, paymentDate, cancellationDate)
 *   → string "YYYY-MM-DD". Se vier Date JS, converte via toISODate (fuso local).
 * - Datas de auditoria (createdAt, updatedAt) → Timestamp nativo do Firebase.
 */
export const financialRecordToFirestore = (data, tenantData, authUser) => {
    const idTenant = typeof tenantData === 'string' ? tenantData : tenantData?.id;
    if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

    // Cálculo proativo do valor líquido
    const grossAmount = Math.max(0, Number(data.grossAmount) || 0);
    const feePercentage = Math.max(0, Number(data.feePercentage) || 0);
    const feeAmount = Math.max(0, Number(data.feeAmount) || 0);
    const discount = Math.max(0, Number(data.discount) || 0);
    const fineAndInterest = Math.max(0, Number(data.fineAndInterest) || 0);
    
    // Líquido = (Bruto + Multa/Juros) - Desconto - Taxa
    const netAmount = (grossAmount + fineAndInterest) - discount - feeAmount;

    // Se a data de vencimento passou e não está pago, classifica OVERDUE
    const today = toISODate(new Date());
    let computedStatus = data.status || "PENDING";
    const dueDateStr = ensureDateString(data.dueDate) || today;
    
    if (computedStatus === "PENDING" && dueDateStr < today) {
        computedStatus = "OVERDUE";
    }

    const payload = {
        idTenant,
        idSale: data.idSale,
        
        // Categoria 
        type: data.type,
        idCategory: data.idCategory,
        categoryName: data.categoryName,

        // Origem do Débito / Crédito
        sourceType: data.sourceType, 
        idEntity: data.idEntity, 
        entityName: data.entityName, 
        
        idMember: data.idMember,
        memberName: data.memberName,
        memberEmail: data.memberEmail,
        memberPhone: data.memberPhone,
        memberPhotoUrl: data.memberPhotoUrl,
        
        idEmployee: data.idEmployee,
        employeeName: data.employeeName,
        employeeEmail: data.employeeEmail,
        employeePhone: data.employeePhone,
        employeePhotoUrl: data.employeePhotoUrl,
        
        idConsultant: data.idConsultant,
        consultantName: data.consultantName,
        consultantEmail: data.consultantEmail,
        consultantPhone: data.consultantPhone,
        consultantPhotoUrl: data.consultantPhotoUrl,

        // Totais Fiscais 
        grossAmount: Number(grossAmount.toFixed(2)),
        feePercentage: Number(feePercentage.toFixed(2)),
        feeAmount: Number(feeAmount.toFixed(2)),
        discount: Number(discount.toFixed(2)),
        fineAndInterest: Number(fineAndInterest.toFixed(2)),
        netAmount: Number(netAmount.toFixed(2)),

        // Datas de calendário — SEMPRE string "YYYY-MM-DD"
        issueDate: ensureDateString(data.issueDate),
        competenceDate: ensureDateString(data.competenceDate),
        dueDate: ensureDateString(data.dueDate),
        paymentDate: ensureDateString(data.paymentDate),
        cancellationDate: ensureDateString(data.cancellationDate),
        
        // Bancário
        idBankAccount: data.idBankAccount,       
        bankAccountName: data.bankAccountName,
        paymentMethod: data.paymentMethod,
        currentInstallment: Number(data.currentInstallment),   
        totalInstallments: Number(data.totalInstallments),      
        documentNumber: data.documentNumber,      

        status: computedStatus,

        cancellationReason: data.cancellationReason,
        notes: data.notes,

        // Auditoria — SEMPRE Timestamp nativo do Firebase
        createdAt: data.id ? (data.createdAt || Timestamp.now()) : Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: data.id ? (data.createdBy || null) : {
            uid: authUser?.uid || "system",
            name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
        },
        updatedBy: {
            uid: authUser?.uid || "system",
            name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
        }
    };

    Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
    return payload;
};

/**
 * financialRecordFromFirestore
 * 
 * Converte dados do Firestore para o formato do React.
 * Datas de calendário já são strings "YYYY-MM-DD" no banco (sem fallback).
 * Datas de auditoria (Timestamp) são convertidas para Date JS.
 */
export const financialRecordFromFirestore = (snapshot) => {
    if (!snapshot.exists()) return null;
    const data = snapshot.data();

    return {
        id: snapshot.id,
        ...data,
        // Datas de calendário — já são strings "YYYY-MM-DD" no banco
        issueDate: data.issueDate,
        competenceDate: data.competenceDate,
        dueDate: data.dueDate,
        paymentDate: data.paymentDate,
        cancellationDate: data.cancellationDate,
        // Datas de auditoria — Timestamp → Date JS
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
    };
};

/**
 * Garante que o valor é uma string "YYYY-MM-DD" ou null.
 * Aceita: string "YYYY-MM-DD", Date JS. Rejeita: qualquer outra coisa.
 */
function ensureDateString(value) {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (value instanceof Date) return toISODate(value);
    return null;
}
